import React, { useState, useEffect, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import ReactMarkdown from "react-markdown"
import {
  Shield, X, Check, Copy, Bookmark, Share2, MoreHorizontal, Download, PlayCircle, ChevronLeft, ChevronRight,
} from "lucide-react"
import SEO from "../../components/Common/SEO"
import { storeValidatorApi } from "../../services/storeValidatorApi"
import { INITIAL_FORM, buildFormPatchFromRecord, assetSrc, RESPONSIVE_CSS, Skeleton, extractIarcInsights } from "./StoreValidator"

// Thumbnail-strip scroll arrows stay invisible until the strip itself is hovered — a pure CSS
// rule since inline styles can't express :hover state.
// opacity lives entirely in CSS (not the inline style prop) because inline styles always
// beat a stylesheet rule — including :hover — regardless of selector specificity, so a base
// opacity: 0 set inline could never be overridden by the hover rule below.
const PREVIEW_STYLE = `
  .sv-strip-arrow { opacity: 0; transition: opacity 0.15s; }
  .sv-media-strip:hover .sv-strip-arrow { opacity: 1; }
  html, body { overscroll-behavior-y: none; }
`

// The five official IARC/PEGI age brackets. A predicted board's own rating string (e.g. "12+")
// carries a number that doesn't always land exactly on one of these — map it up to the nearest
// bracket at or above it, same way a real rating body would never under-state the age floor.
const IARC_AGE_BRACKETS = [3, 7, 12, 16, 18]
const resolveIarcAge = predicted => {
  const match = String(predicted || "").match(/\d+/)
  if (!match) return null
  const n = parseInt(match[0], 10)
  return IARC_AGE_BRACKETS.find(b => n <= b) ?? IARC_AGE_BRACKETS[IARC_AGE_BRACKETS.length - 1]
}

// These are real multi-MB files served straight from S3 (no thumbnail/CDN resizing on the
// backend), so they can't be made to load faster from here — but a blurred placeholder that
// sharpens once the real bytes arrive feels far faster than a blank box or a hard pop-in.
// Callers MUST pass key={src} so React remounts this (resetting the blur) whenever the image
// being shown actually changes, instead of reusing a stale "already loaded" state.
const BlurImage = ({ src, alt = "", style, onClick, draggable }) => {
  const [loaded, setLoaded] = useState(false)
  return (
    <img
      src={src}
      alt={alt}
      draggable={draggable}
      onClick={onClick}
      onLoad={() => setLoaded(true)}
      style={{
        ...style,
        filter: loaded ? "none" : "blur(16px)",
        transition: "filter 0.25s ease",
      }}
    />
  )
}

// Standalone page (mounted outside the site's Layout — see the isReport-style special case in
// App.js) so it renders with NO header/sidebar, matching how a real Meta Horizon Store listing
// page — or any shared link to one — would look. Fetches the real persisted listing from
// GET /shared/<app_id>/ (works for any authenticated user once status is "in_review", per the
// API doc); falls back to GET /history/<app_id>/ for the owner's own view of a draft, since
// /shared/ 403s before submission.
const StoreValidatorPreview = () => {
  const [searchParams] = useSearchParams()
  const appId = searchParams.get("app")
  const [record, setRecord] = useState(null)
  const [loading, setLoading] = useState(true)
  const [previewTheme, setPreviewTheme] = useState("meta")
  const [aboutExpanded, setAboutExpanded] = useState(false)
  const [previewImage, setPreviewImage] = useState(null)
  const [copied, setCopied] = useState(false)
  const [activeMediaIndex, setActiveMediaIndex] = useState(0)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const dragStartXRef = useRef(null)
  const stripRef = useRef(null)
  const [stripScroll, setStripScroll] = useState({ left: false, right: false })
  const updateStripScroll = () => {
    const el = stripRef.current
    if (!el) return
    setStripScroll({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    })
  }

  useEffect(() => {
    if (!appId) { setLoading(false); return }
    let cancelled = false
    const load = async () => {
      try {
        const shared = await storeValidatorApi.getSharedApp(appId)
        if (!cancelled) setRecord(shared)
      } catch (e) {
        console.error("Failed to load shared app, falling back to history:", e)
        try {
          const history = await storeValidatorApi.getAppHistory(appId)
          if (!cancelled) setRecord(history)
        } catch (e2) {
          console.error("Failed to load app history for preview:", e2)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [appId])

  const copyPreviewLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      console.error("Failed to copy preview link:", e)
    }
  }

  const previewFormData = { ...INITIAL_FORM, ...(record ? buildFormPatchFromRecord(record) : {}) }
  const genrePills = [previewFormData.primaryGenre, previewFormData.additionalGenre1, previewFormData.additionalGenre2].filter(Boolean)
  // Same source and extraction (extractIarcInsights) the editor itself uses to show this —
  // record.sections.iarc_questionnaire.predicted_rating, confirmed live on /history/<app_id>/
  // (getAppHistory, this page's own fallback fetch above). Prefer a board literally named IARC,
  // then any board whose predicted value is already in the plain "N+" age-bracket format (PEGI
  // and IARC's own unified badge both use this), then ESRB, then whatever's first — this is
  // still an ESTIMATE, never the real IARC/ESRB/PEGI result.
  const predictedRating = record ? extractIarcInsights(record.sections?.iarc_questionnaire)?.predictedRating : null
  const ratingAuthorities = Object.entries(predictedRating?.authorities || {})
  const primaryRatingEntry = ratingAuthorities.find(([board]) => board.toUpperCase() === "IARC")
    || ratingAuthorities.find(([, data]) => /^\d+\+?$/.test(String(data?.predicted || "").trim()))
    || ratingAuthorities.find(([board]) => board.toUpperCase() === "ESRB")
    || ratingAuthorities[0] || null
  const [, primaryRatingData] = primaryRatingEntry || []
  const iarcAge = resolveIarcAge(primaryRatingData?.predicted)
  // Matches the real badge's two description lines (screenshot, 2026-10-21): a content-category
  // line ("Horror") from flagged_categories, and an interactive-elements line ("Users Interact,
  // In-Game Purchases") from the predicted authority's own driven_by list.
  const flaggedCategoryNames = Object.keys(predictedRating?.flagged_categories || {})
  // Confirmed real field (2026-10-21): content_descriptors (e.g. "User Interaction") — not
  // driven_by, which was this page's earlier guess before the actual field name was confirmed.
  // Exact nesting wasn't given alongside the field name, so check every plausible location: per
  // the predicted authority itself (my first guess), top-level on predicted_rating (one shared
  // list for whichever badge is shown), and as a sibling of predicted_rating on the section
  // itself (record.sections.iarc_questionnaire) rather than nested inside it.
  const contentDescriptors =
    primaryRatingData?.content_descriptors
    || predictedRating?.content_descriptors
    || record?.sections?.iarc_questionnaire?.content_descriptors
    || []
  // Computed once (not inline at each usage) since assetSrc() calls URL.createObjectURL() for
  // a local File, which returns a brand new string every call even for the same file.
  const iconSrc = previewFormData.appIcon ? assetSrc(previewFormData.appIcon) : null
  const isMeta = previewTheme === "meta"
  // Two palettes for the same layout — "meta" matches a real Meta Horizon Store listing page
  // (verified against screenshots, 2026-08-25); "app" reskins it in our own dark theme.
  const c = {
    pageBg: isMeta ? "#ffffff" : "#171821",
    cardBg: isMeta ? "#ffffff" : "#21222D",
    text: isMeta ? "#0a0a0a" : "#fff",
    textMuted: isMeta ? "#65676b" : "#9ca3af",
    textFaint: isMeta ? "#8a8d91" : "#6b7280",
    divider: isMeta ? "#e4e6eb" : "#2E3244",
    cardBorder: isMeta ? "#e4e6eb" : "#2E3244",
    pillBorder: isMeta ? "#d1d5db" : "#2E3244",
    accent: isMeta ? "#1877F2" : "#F6255A",
    mediaBg: isMeta ? "#171821" : "#21222D",
  }
  // Forcefully overscrolling past the top/bottom (rubber-band bounce, mainly macOS trackpads)
  // reveals whatever's behind the page — the site's own default background, which doesn't
  // match either preview theme — so sync it to the current theme's page color for the
  // duration of this page, restoring on unmount/theme change.
  useEffect(() => {
    const prevHtmlBg = document.documentElement.style.background
    const prevBodyBg = document.body.style.background
    document.documentElement.style.background = c.pageBg
    document.body.style.background = c.pageBg
    return () => {
      document.documentElement.style.background = prevHtmlBg
      document.body.style.background = prevBodyBg
    }
  }, [c.pageBg])

  const linkStyle = { display: "inline-flex", alignItems: "center", gap: "6px", color: c.textMuted, fontSize: "13px", textDecoration: "none" }
  // Deliberately excluded from this list (matches how a real store listing works, not fields
  // we forgot): keywords (search metadata, never shown to shoppers), artStyle (internal
  // tagging), countryOfOrigin/developerContactEmail/iarcCertId (back-office/compliance info,
  // not public), and the genre sub-type pickers (actionType/sportsType/precisionSportsType —
  // a niche refinement of genre, not separately displayed).
  const detailRows = [
    { label: "Game Modes", value: (previewFormData.playerModes || []).join(", ") },
    { label: "Supported Player Modes", value: (previewFormData.physicalModes || []).join(", ") },
    { label: "Supported Controllers", value: (previewFormData.supportedInput || []).join(", ") },
    { label: "Play area", value: previewFormData.playArea },
    { label: "Category", value: previewFormData.category },
    { label: "Genres", value: genrePills.join(", ") },
    { label: "Theme", value: (previewFormData.gameTheme || []).join(", ") },
    { label: "Gameplay challenge", value: previewFormData.gameplayChallenge },
    { label: "Gameplay mechanics", value: (previewFormData.gameplayMechanics || []).join(", ") },
    { label: "Languages", value: (previewFormData.supportedLanguages || []).join(", ") },
    { label: "Developer", value: previewFormData.developer },
    { label: "Publisher", value: previewFormData.publisher },
    { label: "Website", value: previewFormData.websiteUrl, href: previewFormData.websiteUrl },
    { label: "Developer Privacy Policy", value: previewFormData.privacyPolicy ? "Privacy Policy" : "", href: previewFormData.privacyPolicy },
    { label: "Support", value: previewFormData.supportUrl ? "Get support" : "", href: previewFormData.supportUrl },
    { label: "Terms of Service", value: previewFormData.termsOfService ? "Terms of Service" : "", href: previewFormData.termsOfService },
    { label: "Comfort level", value: previewFormData.comfortLevel },
    { label: "Internet connection", value: previewFormData.internetConnection && `Internet connection ${previewFormData.internetConnection.toLowerCase()}` },
    { label: "Subscription", value: previewFormData.subscription && (previewFormData.subscription.toLowerCase() === "required" ? "Subscription required" : "No subscription required") },
    {
      label: "Social features",
      value: typeof previewFormData.socialFeatures === "string"
        ? (previewFormData.socialFeatures.startsWith("Yes") ? "Uses social features" : "Does not use social features")
        : "",
    },
  ].filter(r => r.value)

  // One unified gallery for every visual asset — trailer, screenshots, and the covers/logo
  // that don't appear on a real listing page itself — so they're all selectable from the same
  // place instead of screenshots living here and the rest sitting separately in the sidebar.
  const mediaItems = [
    previewFormData.trailerUrl && { type: "video", src: assetSrc(previewFormData.trailerUrl), label: "Trailer" },
    ...(previewFormData.screenshots || []).map((f, i) => ({ type: "image", src: assetSrc(f), label: `Screenshot ${i + 1}` })),
    previewFormData.landscapeCover && { type: "image", src: assetSrc(previewFormData.landscapeCover), label: "Landscape cover" },
    previewFormData.squareCover && { type: "image", src: assetSrc(previewFormData.squareCover), label: "Square cover" },
    previewFormData.heroCover && { type: "image", src: assetSrc(previewFormData.heroCover), label: "Hero cover" },
    previewFormData.headerCapsule && { type: "image", src: assetSrc(previewFormData.headerCapsule), label: "Header capsule" },
    previewFormData.appLogo && { type: "image", src: assetSrc(previewFormData.appLogo), label: "Logo" },
  ].filter(Boolean)
  const activeMedia = mediaItems[activeMediaIndex] || null
  const nextMedia = mediaItems.length > 1 ? mediaItems[(activeMediaIndex + 1) % mediaItems.length] : null
  const prevMedia = mediaItems.length > 1 ? mediaItems[(activeMediaIndex - 1 + mediaItems.length) % mediaItems.length] : null

  const goToMedia = delta => {
    if (!mediaItems.length) return
    setActiveMediaIndex(i => (i + delta + mediaItems.length) % mediaItems.length)
  }
  // Mouse/touch drag on the main media box — the current image visibly slides out while the
  // next/previous one slides in from the opposite edge (a real carousel transition, not just
  // the current image moving to reveal empty background), then snaps into place past a small
  // threshold. Pointer capture keeps the drag tracking even if the cursor moves outside the
  // box mid-gesture.
  const handleMediaPointerDown = e => {
    if (mediaItems.length <= 1) return
    dragStartXRef.current = e.clientX
    setIsDragging(true)
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const handleMediaPointerMove = e => {
    if (dragStartXRef.current == null) return
    setDragOffset(e.clientX - dragStartXRef.current)
  }
  const endMediaDrag = () => {
    if (dragStartXRef.current == null) return
    const delta = dragOffset
    dragStartXRef.current = null
    setIsDragging(false)
    setDragOffset(0)
    if (Math.abs(delta) > 40) goToMedia(delta < 0 ? 1 : -1)
  }
  // Video has no static frame to peek at mid-drag, so it's represented by a simple dark
  // "poster" (same icon the thumbnail strip uses) while sliding; the real playable <video>
  // still renders normally once it's the settled, non-dragging active item.
  const renderMediaLayer = item => (
    item?.type === "video"
      ? <div style={{ width: "100%", height: "100%", background: "#000", display: "flex", alignItems: "center", justifyContent: "center" }}><PlayCircle size={40} color="#fff" style={{ opacity: 0.85 }} /></div>
      : <BlurImage key={item?.src} src={item?.src} draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
  )

  useEffect(() => { updateStripScroll() }, [mediaItems.length])

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: c.pageBg }}>
        <style>{RESPONSIVE_CSS}</style>
        <div className="sv-preview-columns" style={{ maxWidth: "1200px", margin: "0 auto", padding: "64px 40px 80px", display: "flex", gap: "36px", alignItems: "flex-start" }}>
          <div className="sv-preview-main" style={{ flex: "2.2 1 0%", minWidth: 0 }}>
            <Skeleton height="0" radius="14px" style={{ paddingBottom: "56.25%", marginBottom: "24px" }} />
            <Skeleton width="70%" height="20px" style={{ marginBottom: "14px" }} />
            <Skeleton width="100%" height="13px" style={{ marginBottom: "8px" }} />
            <Skeleton width="90%" height="13px" style={{ marginBottom: "8px" }} />
            <Skeleton width="60%" height="13px" />
          </div>
          <div className="sv-preview-sidebar" style={{ flex: "1 1 0%", minWidth: "300px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "24px" }}>
              <Skeleton width="64px" height="64px" radius="16px" />
              <div style={{ flex: 1 }}>
                <Skeleton width="80%" height="15px" style={{ marginBottom: "8px" }} />
                <Skeleton width="50%" height="12px" />
              </div>
            </div>
            {[0, 1, 2, 3].map(i => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: "10px", padding: "10px 0", borderBottom: `1px solid ${c.cardBorder}` }}>
                <Skeleton width="30%" height="12px" />
                <Skeleton width="40%" height="12px" />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (!record) {
    return (
      <div style={{ minHeight: "100vh", background: "#171821", color: "#9ca3af", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px", fontSize: "13px" }}>
        <span>Couldn't load this app's preview.</span>
        <a href={`/store-validator?app=${appId}`} style={{ color: "#F6255A" }}>Back to editor</a>
      </div>
    )
  }

  return (
    <div style={{ minHeight: "100vh", background: c.pageBg, color: c.text }}>
      <style>{RESPONSIVE_CSS}</style>
      <style>{PREVIEW_STYLE}</style>
      <SEO title={`Preview — ${previewFormData.appName || "New App"}`} />

      {/* Preview chrome bar — this stays our own tool's dark UI, not part of the mock page */}
      <div style={{ position: "fixed", top: "16px", right: "16px", zIndex: 10, display: "flex", alignItems: "center", gap: "14px" }}>
        <button
          onClick={copyPreviewLink}
          style={{ display: "flex", alignItems: "center", gap: "6px", background: c.cardBg, border: `1px solid ${c.cardBorder}`, color: copied ? "#22c55e" : c.textMuted, borderRadius: "7px", padding: "6px 12px", fontSize: "12px", fontWeight: 600, cursor: "pointer", boxShadow: "0 4px 16px rgba(0,0,0,0.18)" }}
        >
          {copied ? <><Check size={13} /> Copied!</> : <><Copy size={13} /> Copy link</>}
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: "9px", background: c.cardBg, border: `1px solid ${c.cardBorder}`, borderRadius: "20px", padding: "6px 12px", boxShadow: "0 4px 16px rgba(0,0,0,0.18)" }}>
          <span style={{ color: c.textMuted, fontSize: "12px" }}>Meta theme</span>
          <button
            onClick={() => setPreviewTheme(isMeta ? "app" : "meta")}
            style={{ width: "38px", height: "22px", borderRadius: "11px", background: isMeta ? "#1877F2" : "#2E3244", border: "none", position: "relative", cursor: "pointer", padding: 0 }}
          >
            <span style={{ position: "absolute", top: "2px", left: isMeta ? "18px" : "2px", width: "18px", height: "18px", borderRadius: "50%", background: "#fff", transition: "left 0.15s" }} />
          </button>
        </div>
      </div>

      {/* Left: media + about + details / Right: fixed info card — matches a real listing's
          two-column layout */}
      <div className="sv-preview-columns" style={{ maxWidth: "1200px", margin: "0 auto", padding: "64px 40px 80px", display: "flex", gap: "36px", alignItems: "flex-start" }}>
        <div className="sv-preview-main" style={{ flex: "2.2 1 0%", minWidth: 0 }}>
          {/* Media — every visual asset in one gallery (trailer, screenshots, covers, logo).
              Click a thumbnail to select it, or drag left/right on the big image: it slides
              out while the next/previous item slides in from the opposite edge. */}
          <div
            onPointerDown={handleMediaPointerDown}
            onPointerMove={handleMediaPointerMove}
            onPointerUp={endMediaDrag}
            onPointerCancel={endMediaDrag}
            style={{ position: "relative", width: "100%", aspectRatio: "16/9", borderRadius: "8px", overflow: "hidden", background: c.mediaBg, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "10px", cursor: mediaItems.length > 1 ? (isDragging ? "grabbing" : "grab") : "default", userSelect: "none", touchAction: "pan-y" }}
          >
            {activeMedia ? (
              <>
                {/* Active layer — a real playable <video> once settled; a simplified poster
                    while dragging (matches the peeking neighbor layer below it). */}
                <div style={{ position: "absolute", inset: 0, transform: `translateX(${dragOffset}px)`, transition: isDragging ? "none" : "transform 0.2s ease" }}>
                  {activeMedia.type === "video" && !isDragging
                    ? <video src={activeMedia.src} controls style={{ width: "100%", height: "100%", objectFit: "contain", background: "#000" }} />
                    : renderMediaLayer(activeMedia)}
                </div>
                {isDragging && dragOffset < 0 && nextMedia && (
                  <div style={{ position: "absolute", inset: 0, transform: `translateX(calc(100% + ${dragOffset}px))` }}>
                    {renderMediaLayer(nextMedia)}
                  </div>
                )}
                {isDragging && dragOffset > 0 && prevMedia && (
                  <div style={{ position: "absolute", inset: 0, transform: `translateX(calc(-100% + ${dragOffset}px))` }}>
                    {renderMediaLayer(prevMedia)}
                  </div>
                )}
              </>
            ) : (
              <span style={{ color: "#4b5563", fontSize: "13px" }}>No media uploaded yet</span>
            )}
          </div>
          {mediaItems.length > 0 && (
            <div
              className="sv-media-strip"
              style={{ position: "relative", marginBottom: "28px" }}
            >
              <div ref={stripRef} onScroll={updateStripScroll} style={{ display: "flex", gap: "8px", overflowX: "auto", scrollBehavior: "smooth" }}>
                {mediaItems.map((m, i) => (
                  <button
                    key={`${m.label}-${i}`}
                    onClick={() => setActiveMediaIndex(i)}
                    title={m.label}
                    style={{ width: "110px", height: "62px", borderRadius: "6px", overflow: "hidden", background: c.mediaBg, flexShrink: 0, border: i === activeMediaIndex ? `2px solid ${c.accent}` : "1px solid transparent", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    {m.type === "video"
                      ? <PlayCircle size={20} color="#fff" />
                      : <BlurImage key={m.src} src={m.src} draggable={false} style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                  </button>
                ))}
              </div>
              {/* Hidden until hovering the strip, and only shown on the side there's actually
                  more to scroll to. */}
              {stripScroll.left && (
                <button
                  className="sv-strip-arrow"
                  onClick={() => stripRef.current?.scrollBy({ left: -200, behavior: "smooth" })}
                  style={{ position: "absolute", left: "-4px", top: "50%", transform: "translateY(-50%)", width: "26px", height: "26px", borderRadius: "50%", background: c.cardBg, border: `1px solid ${c.cardBorder}`, color: c.textMuted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.2)" }}
                >
                  <ChevronLeft size={14} />
                </button>
              )}
              {stripScroll.right && (
                <button
                  className="sv-strip-arrow"
                  onClick={() => stripRef.current?.scrollBy({ left: 200, behavior: "smooth" })}
                  style={{ position: "absolute", right: "-4px", top: "50%", transform: "translateY(-50%)", width: "26px", height: "26px", borderRadius: "50%", background: c.cardBg, border: `1px solid ${c.cardBorder}`, color: c.textMuted, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.2)" }}
                >
                  <ChevronRight size={14} />
                </button>
              )}
            </div>
          )}

          {/* About — no tabs (reviews don't apply pre-publish, "more" content folded in below) */}
          <div style={{ marginBottom: "28px" }}>
            {previewFormData.shortDescription && <p style={{ fontSize: "15px", fontWeight: 600, margin: "0 0 10px", lineHeight: 1.5 }}>{previewFormData.shortDescription}</p>}
            {previewFormData.longDescription && (
              <>
                <div style={{
                  fontSize: "14px", color: c.textMuted, lineHeight: 1.7,
                  display: aboutExpanded ? "block" : "-webkit-box",
                  WebkitLineClamp: aboutExpanded ? "unset" : 3,
                  WebkitBoxOrient: "vertical", overflow: aboutExpanded ? "visible" : "hidden",
                }}>
                  <ReactMarkdown components={{
                    p: ({ node, ...p }) => <p {...p} style={{ margin: "0 0 10px" }} />,
                    strong: ({ node, ...p }) => <strong {...p} style={{ color: c.text }} />,
                    a: ({ node, children, ...p }) => <a {...p} target="_blank" rel="noreferrer" style={{ color: c.accent }}>{children}</a>,
                    img: ({ node, alt, ...p }) => <img {...p} alt={alt || ""} style={{ maxWidth: "100%", borderRadius: "6px", margin: "6px 0", display: "block" }} />,
                    ul: ({ node, ...p }) => <ul {...p} style={{ margin: "0 0 10px", paddingLeft: "20px" }} />,
                    ol: ({ node, ...p }) => <ol {...p} style={{ margin: "0 0 10px", paddingLeft: "20px" }} />,
                  }}>
                    {previewFormData.longDescription}
                  </ReactMarkdown>
                </div>
                {!aboutExpanded && (
                  <button onClick={() => setAboutExpanded(true)} style={{ background: "none", border: "none", color: c.accent, fontSize: "13px", fontWeight: 600, cursor: "pointer", padding: "4px 0" }}>more...</button>
                )}
              </>
            )}
            {!previewFormData.shortDescription && !previewFormData.longDescription && <p style={{ fontSize: "13px", color: "#6b7280", margin: 0 }}>No description added yet.</p>}
          </div>

          {previewFormData.whatsNew && (
            <div style={{ marginBottom: "28px" }}>
              <h2 style={{ fontSize: "20px", fontWeight: 700, margin: "0 0 12px" }}>Updates</h2>
              <p style={{ fontSize: "14px", color: c.textMuted, margin: 0, lineHeight: 1.6, whiteSpace: "pre-line" }}>{previewFormData.whatsNew}</p>
            </div>
          )}

          {detailRows.length > 0 && (
            <div>
              <h2 style={{ fontSize: "20px", fontWeight: 700, margin: "0 0 4px" }}>Additional details</h2>
              {detailRows.map(r => (
                <div key={r.label} className="sv-detail-row" style={{ display: "flex", gap: "24px", padding: "14px 0", borderBottom: `1px solid ${c.divider}` }}>
                  <div className="sv-detail-label" style={{ width: "220px", flexShrink: 0, fontSize: "14px" }}>{r.label}</div>
                  <div style={{ fontSize: "14px" }}>
                    {r.href ? <a href={r.href} target="_blank" rel="noreferrer" style={{ color: c.accent, textDecoration: "underline" }}>{r.value}</a> : r.value}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Privacy — kept here too since we don't have granular permission fields to build
              the real page's full "Sensor & device data" breakdown */}
          <div style={{ paddingTop: "24px", marginTop: "24px", borderTop: `1px solid ${c.divider}` }}>
            <h2 style={{ fontSize: "20px", fontWeight: 700, margin: "0 0 8px" }}>App privacy</h2>
            <p style={{ fontSize: "13px", color: c.textMuted, margin: 0, lineHeight: 1.6 }}>
              {typeof previewFormData.socialFeatures === "string" && previewFormData.socialFeatures.startsWith("Yes")
                ? "This app may access your display name, username, and profile picture to support its social features."
                : "The developer has not disclosed what data this app may access."}
            </p>
          </div>
        </div>

        {/* Right: fixed info card */}
        <div className="sv-preview-sidebar" style={{ flex: "1 1 0%", minWidth: "300px", position: "sticky", top: "64px" }}>
          <div style={{ background: c.cardBg, border: `1px solid ${c.cardBorder}`, borderRadius: "14px", padding: "24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "14px", marginBottom: "8px" }}>
              {previewFormData.appIcon && (
                <BlurImage
                  key={iconSrc}
                  src={iconSrc}
                  onClick={() => setPreviewImage(iconSrc)}
                  style={{ width: "64px", height: "64px", borderRadius: "16px", objectFit: "cover", flexShrink: 0, border: `1px solid ${c.cardBorder}`, cursor: "zoom-in" }}
                />
              )}
              <h1 style={{ fontSize: "26px", fontWeight: 800, margin: 0 }}>{previewFormData.appName || "Untitled App"}</h1>
            </div>
            {/* This line is about user review stars (we have no real review data), not content
                rating — the real Meta listing's IARC badge is its own separate block below the
                action buttons (see screenshot, 2026-10-21), not merged in here. */}
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px", color: c.textMuted, fontSize: "13px", marginBottom: "14px" }}>
              <span>No ratings yet</span>
              {genrePills.length > 0 && <span>• {genrePills.join(" • ")}</span>}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "16px" }}>
              <span style={{ ...linkStyle, border: `1px solid ${c.pillBorder}`, borderRadius: "20px", padding: "6px 14px" }}><Bookmark size={13} /> Add to wishlist</span>
              <span style={{ ...linkStyle, border: `1px solid ${c.pillBorder}`, borderRadius: "20px", padding: "6px 14px" }}><Share2 size={13} /> Share</span>
              <span style={{ ...linkStyle, border: `1px solid ${c.pillBorder}`, borderRadius: "20px", padding: "6px 14px" }}><MoreHorizontal size={13} /> More</span>
            </div>
            {/* Matches the real Meta listing's IARC badge exactly (screenshot, 2026-10-21): a
                fixed-design two-tone square — a solid black band carrying the "IARC" label, white
                age number panel below it — kept at these exact colors regardless of the page's
                own light/dark theme toggle, since the real badge never re-themes itself either.
                Next to it, the age repeated as a heading, then one line of flagged content
                categories and one line of the predicted authority's own content_descriptors. */}
            {iarcAge != null && (
              <div style={{ display: "flex", gap: "14px", marginBottom: "18px" }}>
                <div style={{ width: "46px", height: "46px", flexShrink: 0, border: "1.5px solid #000", borderRadius: "3px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                  <div style={{ background: "#000", color: "#fff", fontSize: "9.5px", fontWeight: 800, letterSpacing: "0.01em", textAlign: "center", padding: "3px 0" }}>IARC</div>
                  <div style={{ flex: 1, background: "#fff", color: "#000", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", fontWeight: 800 }}>{iarcAge}+</div>
                </div>
                <div>
                  <div style={{ fontSize: "16px", fontWeight: 700, textDecoration: "underline", marginBottom: "2px" }}>{iarcAge}+</div>
                  {!!flaggedCategoryNames.length && (
                    <div style={{ fontSize: "13px", color: c.textMuted }}>{flaggedCategoryNames.join(", ")}</div>
                  )}
                  {!!contentDescriptors.length && (
                    <div style={{ fontSize: "13px", color: c.textMuted }}>{contentDescriptors.join(", ")}</div>
                  )}
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: "10px", marginBottom: "18px" }}>
              <Shield size={16} color={c.text} style={{ flexShrink: 0, marginTop: "2px" }} />
              <div>
                <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "3px" }}>Privacy</div>
                <p style={{ fontSize: "12px", color: c.textMuted, margin: 0, lineHeight: 1.5 }}>
                  {typeof previewFormData.socialFeatures === "string" && previewFormData.socialFeatures.startsWith("Yes")
                    ? "This app may access your display name, username, and profile picture."
                    : "The developer has not disclosed what data this app may access."}
                </p>
              </div>
            </div>
            <button style={{ width: "100%", background: c.accent, border: "none", color: "#fff", borderRadius: "24px", padding: "13px 0", fontSize: "14px", fontWeight: 700, cursor: "default", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
              <Download size={15} /> Get
            </button>
          </div>

        </div>
      </div>

      {/* Full-size asset preview lightbox */}
      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
            padding: "40px", cursor: "zoom-out", background: "rgba(10,10,15,0.75)",
            backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
          }}
        >
          <BlurImage
            key={previewImage}
            src={previewImage}
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: "min(80vw, 900px)", maxHeight: "80vh", borderRadius: "10px", boxShadow: "0 20px 60px rgba(0,0,0,0.6)", cursor: "default" }}
          />
          <button
            onClick={() => setPreviewImage(null)}
            style={{ position: "fixed", top: "24px", right: "24px", width: "42px", height: "42px", borderRadius: "50%", background: "#fff", border: "none", color: "#171821", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(0,0,0,0.35)" }}
          >
            <X size={20} strokeWidth={2.5} />
          </button>
        </div>
      )}
    </div>
  )
}

export default StoreValidatorPreview
